import React, { useState, useEffect, useContext } from "react";
import {
  Box, Input, Button, Heading, FormControl, FormLabel, Grid, GridItem,
  Select, Checkbox, VStack, Text, FormErrorMessage, Image, HStack
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../services/ApiService";
import ThaiDatePicker from "../utils/ThaiDatePicker";
import { AuthContext } from "../context/AuthContext";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard, faLocationDot, faImage, faUser } from "@fortawesome/free-solid-svg-icons";

const RegistrationOfficeStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useContext(AuthContext) || {};
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    DLDEmployeeID: "",
    FirstName: "",
    LastName: "",
    FirstName2: "",
    LastName2: "",
    Gender: "M",
    OfficeType: "",
    OfficeID: "",
    DepartmentName: "",
    Position: "",
    Email: "",
    Phone: "",
    Address: "",
    ProfileImage: null,
    Remark: "",
    Status: "1",
    Password: "",
    ConfirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);

  useEffect(() => {
    const employeeId = new URLSearchParams(location.search).get("id");
    if (employeeId) {
      setIsEditMode(true);
      fetchEmployeeData(employeeId);
    }
  }, [location.search]);

  const fetchEmployeeData = async (employeeId) => {
    try {
      const data = await ApiService({
        method: "GET",
        url: `/getDLDEmployeeByID?id=${employeeId}`,
        sessionId,
      });
      setFormData({
        ...data,
        Status: data.Status ? "1" : "0",
        Password: "",
        ConfirmPassword: "",
      });
      setPrivacyChecked(true);
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล: " + error.message);
    }
  };

  useEffect(() => {
    const fetchOfficeData = async () => {
      try {
        const officeTypeResponse = await ApiService({ method: "POST", url: "/getDLDOfficeTypeList", sessionId });
        const officeResponse = await ApiService({ method: "POST", url: "/getDLDOfficeList", sessionId });
        setOfficeTypes(officeTypeResponse.OfficeTypeList || []);
        setOffices(officeResponse.OfficeList || []);
      } catch (error) {
        console.error("Error fetching office data:", error);
      }
    };
    fetchOfficeData();
  }, [sessionId]);

  useEffect(() => {
    if (formData.OfficeType) {
      const filtered = offices.filter(office => office.OfficeType === formData.OfficeType);
      setFilteredOffices(filtered);
      if (!filtered.some(office => office.OfficeID === formData.OfficeID)) {
        setFormData(prev => ({ ...prev, OfficeID: "" }));
      }
    } else {
      setFilteredOffices(offices);
    }
  }, [formData.OfficeType, offices]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({ ...formData, ProfileImage: reader.result });
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DLDEmployeeID":
        if (!value) {
          newErrors[name] = "กรุณากรอกรหัสเจ้าหน้าที่";
        } else if (value.length > 30) {
          newErrors[name] = "รหัสเจ้าหน้าที่ต้องไม่เกิน 30 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "รูปแบบอีเมลไม่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "Password":
        if (!isEditMode && !value) {
          newErrors[name] = "กรุณากรอกรหัสผ่าน";
        } else if (value && value.length < 6) {
          newErrors[name] = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "ConfirmPassword":
        if (!isEditMode && !value) {
          newErrors[name] = "กรุณายืนยันรหัสผ่าน";
        } else if (value && value !== formData.Password) {
          newErrors[name] = "รหัสผ่านไม่ตรงกัน";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        if (!value && ["FirstName", "LastName", "Email", "Phone", "OfficeType", "OfficeID", "DepartmentName", "Position"].includes(name)) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    if (isEditMode) {
      return (
        formData.DLDEmployeeID.trim() !== "" &&
        formData.DLDEmployeeID.length <= 30 &&
        formData.FirstName.trim() !== "" &&
        formData.LastName.trim() !== "" &&
        formData.Email.trim() !== "" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
        formData.Phone.trim() !== "" &&
        formData.OfficeType.trim() !== "" &&
        formData.OfficeID.trim() !== "" &&
        formData.DepartmentName.trim() !== "" &&
        formData.Position.trim() !== "" &&
        (!formData.Password || (formData.Password && formData.ConfirmPassword === formData.Password)) &&
        Object.keys(errors).length === 0 &&
        privacyChecked
      );
    }
    return (
      formData.DLDEmployeeID.trim() !== "" &&
      formData.DLDEmployeeID.length <= 30 &&
      formData.FirstName.trim() !== "" &&
      formData.LastName.trim() !== "" &&
      formData.Email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone.trim() !== "" &&
      formData.OfficeType.trim() !== "" &&
      formData.OfficeID.trim() !== "" &&
      formData.DepartmentName.trim() !== "" &&
      formData.Position.trim() !== "" &&
      formData.Password.trim() !== "" &&
      formData.ConfirmPassword === formData.Password &&
      Object.keys(errors).length === 0 &&
      privacyChecked
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
      return;
    }

    const submitData = {
      ...formData,
      ProfileImage: formData.ProfileImage || "",
      Status: formData.Status === "1" ? 1 : 0,
    };

    try {
      if (isEditMode) {
        await ApiService({
          method: "POST",
          url: "/updateDLDEmployee",
          data: submitData,
          sessionId,
        });
        alert("แก้ไขข้อมูลสำเร็จ!");
      } else {
        await ApiService({
          method: "POST",
          url: "/registerDLDEmployee",
          data: submitData,
          sessionId,
        });
        alert("ลงทะเบียนสำเร็จ!");
      }
      navigate("/home");
    } catch (error) {
      alert(`เกิดข้อผิดพลาดในการ${isEditMode ? "แก้ไข" : "ลงทะเบียน"}: ` + error.message);
    }
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        {isEditMode ? "แก้ไขข้อมูลเจ้าหน้าที่" : "ลงทะเบียนเจ้าหน้าที่"}
      </Heading>

      {/* Box 1: ข้อมูลทั่วไป */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iTeal.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faAddressCard} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">ข้อมูลทั่วไป</Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          {["DLDEmployeeID", "FirstName", "LastName", "Gender", "Position"].map((key) => (
            <GridItem key={key} colSpan={key === "DLDEmployeeID" ? 1 : 1}>
              <FormControl isRequired isInvalid={!!errors[key]}>
                <FormLabel fontSize="xl" color="gray">
                  {key === "DLDEmployeeID" ? "รหัสเจ้าหน้าที่" :
                   key === "FirstName" ? "ชื่อ" :
                   key === "LastName" ? "นามสกุล" :
                   key === "Gender" ? "เพศ" : "ตำแหน่ง"}
                  <Text as="span" color="red"> *</Text>
                </FormLabel>
                {key === "Gender" ? (
                  <Select name={key} value={formData[key]} onChange={handleInputChange} colorScheme="iTeal">
                    <option value="M">ชาย</option>
                    <option value="F">หญิง</option>
                  </Select>
                ) : (
                  <Input
                    name={key}
                    value={formData[key]}
                    onChange={handleInputChange}
                    colorScheme="iTeal"
                    isDisabled={key === "DLDEmployeeID" && isEditMode} // Disable in edit mode
                  />
                )}
                <FormErrorMessage>{errors[key]}</FormErrorMessage>
              </FormControl>
            </GridItem>
          ))}
        </Grid>
      </Box>

      {/* Box 2: ข้อมูลหน่วยงาน */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">ข้อมูลหน่วยงาน</Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          <GridItem>
            <FormControl isRequired isInvalid={!!errors["OfficeType"]}>
              <FormLabel fontSize="xl" color="gray">ประเภทสำนักงาน <Text as="span" color="red"> *</Text></FormLabel>
              <Select
                name="OfficeType"
                value={formData.OfficeType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทสำนักงาน"
                colorScheme="iBlue"
              >
                {officeTypes.map((type) => (
                  <option key={type.OfficeTypeID} value={type.OfficeTypeID}>{type.OfficeTypeName}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors["OfficeType"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired isInvalid={!!errors["OfficeID"]}>
              <FormLabel fontSize="xl" color="gray">สำนักงาน <Text as="span" color="red"> *</Text></FormLabel>
              <Select
                name="OfficeID"
                value={formData.OfficeID}
                onChange={handleInputChange}
                placeholder="เลือกหน่วยงาน"
                colorScheme="iBlue"
                isDisabled={!formData.OfficeType}
              >
                {filteredOffices.map((office) => (
                  <option key={office.OfficeID} value={office.OfficeID}>{office.OfficeName}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors["OfficeID"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired isInvalid={!!errors["DepartmentName"]}>
              <FormLabel fontSize="xl" color="gray">หน่วยงาน <Text as="span" color="red"> *</Text></FormLabel>
              <Input
                name="DepartmentName"
                value={formData.DepartmentName}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors["DepartmentName"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      {/* Box 3: ข้อมูลการติดต่อ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iBlue.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faLocationDot} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">ข้อมูลการติดต่อ</Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          {["Email", "Phone", "Address"].map((key) => (
            <GridItem colSpan={key === "Address" ? 3 : 1} key={key}>
              <FormControl isRequired={["Email", "Phone"].includes(key)} isInvalid={!!errors[key]}>
                <FormLabel fontSize="xl" color="gray">
                  {key === "Email" ? "อีเมลล์" :
                   key === "Phone" ? "เบอร์โทรศัพท์" : "ที่อยู่"}
                  {["Email", "Phone"].includes(key) ? <Text as="span" color="red"> *</Text> : null}
                </FormLabel>
                <Input name={key} value={formData[key]} onChange={handleInputChange} colorScheme="iBlue"/>
                <FormErrorMessage>{errors[key]}</FormErrorMessage>
              </FormControl>
            </GridItem>
          ))}
        </Grid>
      </Box>

      {/* Box 4: รูปโปรไฟล์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iGreen.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faImage} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">รูปโปรไฟล์</Heading>
          </HStack>
        </Box>
        <Box {...getRootProps()} borderWidth={2} borderStyle="dashed" borderRadius="md" w="70%" p={5} textAlign="center" cursor="pointer">
          <Input {...getInputProps()} colorScheme="iGreen"/>
          {formData.ProfileImage ? (
            <Image src={formData.ProfileImage} alt="Profile Preview" maxH="200px" mx="auto" />
          ) : (
            <Text h={20}>ลากและวางรูปภาพที่นี่ หรือคลิกเพื่อเลือกไฟล์</Text>
          )}
        </Box>
      </Box>

      {/* Box 5: ชื่อผู้ใช้และรหัสผ่าน */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iPurple.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faUser} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">ชื่อผู้ใช้และรหัสผ่าน</Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
          <GridItem>
            <FormControl isRequired isInvalid={!!errors["DLDEmployeeID"]}>
              <FormLabel fontSize="xl" color="gray">ชื่อผู้ใช้ <Text as="span" color="red"> *</Text></FormLabel>
              <Input name="DLDEmployeeID" value={formData.DLDEmployeeID} onChange={handleInputChange} isDisabled colorScheme="iPurple"/>
              <FormErrorMessage>{errors["DLDEmployeeID"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired={!isEditMode} isInvalid={!!errors["Password"]}>
              <FormLabel fontSize="xl" color="gray">รหัสผ่าน {isEditMode ? "" : <Text as="span" color="red"> *</Text>}</FormLabel>
              <Input name="Password" type="password" value={formData.Password} onChange={handleInputChange} colorScheme="iPurple"/>
              <FormErrorMessage>{errors["Password"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired={!isEditMode} isInvalid={!!errors["ConfirmPassword"]}>
              <FormLabel fontSize="xl" color="gray">ยืนยันรหัสผ่าน {isEditMode ? "" : <Text as="span" color="red"> *</Text>}</FormLabel>
              <Input name="ConfirmPassword" type="password" value={formData.ConfirmPassword} onChange={handleInputChange} colorScheme="iPurple"/>
              <FormErrorMessage>{errors["ConfirmPassword"]}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      <Box p={5} m={5}>
        <Checkbox mt={3} onChange={(e) => setPrivacyChecked(e.target.checked)} size="lg" colorScheme="iOrange" isChecked={privacyChecked}>
          ฉันได้อ่านนโยบายความเป็นส่วนตัวและเข้าใจนโยบายความเป็นส่วนตัวของเราแล้ว
          และยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัวของเรา
          และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
        </Checkbox>
      </Box>

      <Box p={5} m={5}>
        <Button colorScheme="blue" mt={5} onClick={handleSubmit} isDisabled={!isFormValid()}>
          {isEditMode ? "บันทึกการแก้ไข" : "ลงทะเบียน"}
        </Button>
      </Box>
    </Box>
  );
};

export default RegistrationOfficeStaff;